import { useId, useMemo, useRef, useState, useEffect, type JSX } from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { TOU_PRESETS, TOU_PRESET_REGIONS, presetWindowsForLoad } from '@/lib/touPresets';
import {
  TOU_WINDOW_MAX_CENTS, ceilingMessageFor, isCentsOverCeiling, parseTouRateText,
} from '@/lib/siteElectricityInput';
import styles from './LocationTouPicker.module.css';

/**
 * Location TOU picker (Commit C polish bundle).
 *
 * Purpose-built for the inline_tou mode of LocationDetailPage. Wraps
 * row-based TOU editing with six pieces of chrome that the shared
 * TimeWindowRows component intentionally doesn't carry (it's used
 * by Tariff TOU preview + Charger guest windows, where most of this
 * polish would be dead weight):
 *
 *   1. Preset templates (PG&E, ConEd, LADWP, Toronto Hydro, etc.)
 *   2. Inline validation (overlap + end>start) with red field borders
 *      + a banner above the rows summarizing issue count.
 *   3. Per-row currency parser: accepts "$0.25", "25¢", "20.3" etc.
 *      and normalizes to cents on blur — display unit follows the
 *      row's rate_type.
 *   4. Copy-day popover: replicates a source day's windows across
 *      target days without disturbing target-day rows that already
 *      cover other hours.
 *   5. 12-hour / 24-hour clock toggle in the header. 12-hour
 *      default matches the PWA's pattern.
 *   6. Schedule preview heatmap: 7×24 grid below the row list,
 *      blue for per-kWh + orange for per-hour, intensity by rate.
 *      Click a cell to highlight the source row in the picker.
 *
 * Save semantics unchanged from Commit B: the server validates
 * overlap on PATCH, so inline validation here is advisory — the
 * Save button stays enabled even with issues, and the structured
 * server error surfaces through getDisplayErrorMessage (commit
 * 9730960). The banner just warns the operator before they hit
 * the round-trip. One exception: a rate above the Site electricity
 * ceiling for a TOU window (80¢, per kWh and per hour alike; Warren
 * 2026-09-27, @evi/shared) is flagged per row here and the Location form
 * holds Save on it, since the wire schema refuses it with only a generic
 * message. Every preset loads clean under it, SCE TOU-D 5-8 PM summer's
 * 74¢ peak included (it was the one row over the earlier 70¢ cap).
 *
 * No new file count for tiny helpers — formatHour12 and parseHour
 * live inline below (parseRate moved to @/lib/siteElectricityInput). Future commit can lift to
 * apps/admin/src/lib/time-format.ts if a second admin caller
 * emerges (today only this picker needs them).
 */

export interface ElectricityTouWindowAdmin {
  day_of_week: number[];
  start_hour: number;
  end_hour: number;
  rate_cents: number;
  rate_type: 'per_kwh' | 'per_hour';
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Issue {
  window_index: number;
  /** end_hour: input field for end_hour is invalid (end <= start).
   *  days_overlap: this window overlaps another on at least one day.
   *  When days_overlap, `days` carries the conflicting day indices
   *  so the picker can highlight just the offending pills.
   *  rate_cents: the rate is above the Site electricity ceiling. */
  field: 'end_hour' | 'days_overlap' | 'rate_cents';
  days?: number[];
  message: string;
}

interface Props {
  value: ElectricityTouWindowAdmin[];
  onChange: (next: ElectricityTouWindowAdmin[]) => void;
  disabled?: boolean;
}

// ── Time helpers (12/24 hour rendering + flexible input parsing) ──

/** Format an hour integer (0-24) as a 12h or 24h string for display.
 *  24 is the end-of-day sentinel — same wall-clock as 0, displayed
 *  as "12 AM" in 12h or "24:00" in 24h. */
function formatHour(h: number, mode: '12h' | '24h'): string {
  if (!Number.isFinite(h) || h < 0 || h > 24) return '';
  if (mode === '24h') {
    return `${String(h).padStart(2, '0')}:00`;
  }
  const hh = Math.floor(h) % 24;
  const period = hh < 12 ? 'AM' : 'PM';
  const display = hh % 12 === 0 ? 12 : hh % 12;
  return `${display} ${period}`;
}

/** Parse a flexible time input — accepts "7", "7am", "7 AM", "07:00",
 *  "13", "1pm", "0", "24" — into a 24-hour integer 0..24, or null
 *  when input is unparseable. Used by both start_hour + end_hour
 *  inputs; range clamp happens at the caller. */
function parseHour(input: string): number | null {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim().toLowerCase().replace(/\s+/g, '');
  if (trimmed === '') return null;
  // Try "HH:MM" first.
  const colonMatch = /^([0-9]{1,2}):([0-9]{2})$/.exec(trimmed);
  if (colonMatch) {
    const h = parseInt(colonMatch[1]!, 10);
    if (Number.isFinite(h) && h >= 0 && h <= 24) return h;
    return null;
  }
  // "7am" / "7pm" / "12am" / "12pm".
  const ampmMatch = /^([0-9]{1,2})(am|pm)$/.exec(trimmed);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1]!, 10);
    const period = ampmMatch[2];
    if (!Number.isFinite(h) || h < 0 || h > 12) return null;
    if (period === 'am') {
      // 12am = midnight (0)
      h = h === 12 ? 0 : h;
    } else {
      // 12pm = noon (12); 1pm-11pm map to 13-23
      h = h === 12 ? 12 : h + 12;
    }
    return h;
  }
  // Plain integer.
  const n = parseInt(trimmed, 10);
  if (Number.isFinite(n) && n >= 0 && n <= 24) return n;
  return null;
}

// ── Currency parsing ────────────────────────────────────────────────

// parseRate lives in @/lib/siteElectricityInput as parseTouRateText: it rounds to
// whole cents (Warren, 2026-09-27) and is unit-tested there.
const parseRate = parseTouRateText;

/** Display normalize a rate value for the input on blur.
 *    per_kwh:  "20.3¢/kWh" (or whole number → "20¢/kWh")
 *    per_hour: "$1.50/hour" (per-hour rates are usually large enough
 *              to read naturally as dollars; under $1 falls back to ¢) */
function formatRate(cents: number, unit: 'per_kwh' | 'per_hour'): string {
  if (!Number.isFinite(cents) || cents < 0) return '';
  if (unit === 'per_hour') {
    if (cents >= 100) {
      return `$${(cents / 100).toFixed(2)}/hour`;
    }
    return `${cents % 1 === 0 ? cents : cents.toFixed(1)}¢/hour`;
  }
  return `${cents % 1 === 0 ? cents : cents.toFixed(1)}¢/kWh`;
}

// ── Validation ──────────────────────────────────────────────────────

function validateTouWindows(windows: ElectricityTouWindowAdmin[]): Issue[] {
  const issues: Issue[] = [];
  // Per-row: end_hour > start_hour. The schema also enforces this on
  // the server, but inline validation catches it pre-submit.
  for (let i = 0; i < windows.length; i += 1) {
    const w = windows[i]!;
    if (w.end_hour <= w.start_hour) {
      issues.push({
        window_index: i,
        field: 'end_hour',
        message: 'End hour must be later than start hour.',
      });
    }
    // The Site electricity ceiling for a TOU window (80¢, per kWh and per
    // hour alike: "a California peak is real and EV2-A sits at 62"; raised
    // from 70 so it stops refusing a published California tariff).
    // Not advisory like the rest: the server refuses it and the Location
    // form holds Save until it is fixed. The rate cell takes CENTS (a
    // bare number is ¢, the unit select reads ¢/kWh · ¢/hour, and the
    // committed value redisplays as ¢ up to a dollar), so the message is
    // the ¢ one; "$0.85" also parses, but lands on screen as 85¢/kWh.
    if (isCentsOverCeiling(w.rate_cents, TOU_WINDOW_MAX_CENTS)) {
      issues.push({
        window_index: i,
        field: 'rate_cents',
        message: ceilingMessageFor('cents', TOU_WINDOW_MAX_CENTS),
      });
    }
  }
  // Per-day overlap: group windows by day, sort by start_hour, look
  // for any adjacent pair where next.start < prev.end. Mirrors the
  // server's overlap validator in apps/api/src/routes/locations/index.ts.
  for (let dow = 0; dow < 7; dow += 1) {
    const rows = windows
      .map((w, idx) => ({ w, idx }))
      .filter(({ w }) => w.day_of_week.includes(dow) && w.end_hour > w.start_hour)
      .sort((a, b) => a.w.start_hour - b.w.start_hour);
    for (let i = 1; i < rows.length; i += 1) {
      const prev = rows[i - 1]!;
      const cur = rows[i]!;
      if (cur.w.start_hour < prev.w.end_hour) {
        // Push days-overlap for BOTH windows so both rows highlight.
        // Caller dedupes by (idx, day) so we don't double-add.
        for (const idx of [prev.idx, cur.idx]) {
          const existing = issues.find(
            (it) => it.window_index === idx && it.field === 'days_overlap',
          );
          if (existing) {
            if (!existing.days?.includes(dow)) existing.days = [...(existing.days ?? []), dow];
          } else {
            issues.push({
              window_index: idx,
              field: 'days_overlap',
              days: [dow],
              message: `Overlaps another window on ${DAY_LABELS[dow]}.`,
            });
          }
        }
      }
    }
  }
  return issues;
}

// ── Component ───────────────────────────────────────────────────────

export function LocationTouPicker({ value, onChange, disabled = false }: Props) {
  const [clockMode, setClockMode] = useState<'12h' | '24h'>('12h');
  const [highlightIdx, setHighlightIdx] = useState<number | null>(null);
  const [copyDayOpen, setCopyDayOpen] = useState(false);
  // Prefix for per-row error ids (aria-describedby targets).
  const idPrefix = useId();

  const issues = useMemo(() => validateTouWindows(value), [value]);
  const issuesByIndex = useMemo(() => {
    const m = new Map<number, Issue[]>();
    for (const it of issues) {
      const arr = m.get(it.window_index) ?? [];
      arr.push(it);
      m.set(it.window_index, arr);
    }
    return m;
  }, [issues]);

  // ── Mutators ──────────────────────────────────────────────────
  const update = (idx: number, next: Partial<ElectricityTouWindowAdmin>): void => {
    onChange(value.map((w, i) => (i === idx ? { ...w, ...next } : w)));
  };
  const remove = (idx: number): void => {
    onChange(value.filter((_, i) => i !== idx));
  };
  const add = (): void => {
    onChange([
      ...value,
      { day_of_week: [], start_hour: 0, end_hour: 24, rate_cents: 0, rate_type: 'per_kwh' },
    ]);
  };
  // Replace/Append toggle drives how `applyPreset` integrates the
  // preset's windows. Replace (default) wipes current state — the
  // common "start fresh from this utility" flow. Append merges the
  // preset's windows into the existing list — used when an operator
  // wants to layer e.g. an idle-fee preset on top of an existing
  // per-kWh schedule. Append almost always produces validator
  // overlap warnings; the operator resolves manually after.
  const [presetMode, setPresetMode] = useState<'replace' | 'append'>('replace');

  const applyPreset = (presetId: string): void => {
    const preset = TOU_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    // Clone the preset's windows to break shared array refs — the
    // preset object lives at module scope and we don't want the
    // user's edits to mutate it — with each rate Math.round-ed to whole
    // cents, the same rule the typed-in rate follows (parseTouRateText).
    // A preset used to write 9.8¢ to the Location verbatim.
    const cloned = presetWindowsForLoad(preset);
    onChange(presetMode === 'replace' ? cloned : [...value, ...cloned]);
  };

  // Most-recent preset attribution — shown below the preset selector
  // so the operator remembers which utility they loaded and the
  // "verify rates" reminder.
  const [appliedPresetId, setAppliedPresetId] = useState<string>('');
  const appliedPreset = TOU_PRESETS.find((p) => p.id === appliedPresetId);

  const toggleDay = (idx: number, day: number): void => {
    const w = value[idx]!;
    const has = w.day_of_week.includes(day);
    update(idx, {
      day_of_week: has
        ? w.day_of_week.filter((d) => d !== day)
        : [...w.day_of_week, day].sort((a, b) => a - b),
    });
  };

  return (
    <div className={styles.picker}>
      {/* Header chrome — presets, copy-day, clock toggle, add */}
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <select
            className={styles.headerSelect}
            value=""
            onChange={(e) => {
              if (e.target.value) {
                applyPreset(e.target.value);
                setAppliedPresetId(e.target.value);
              }
            }}
            disabled={disabled}
            aria-label="Load TOU preset"
          >
            <option value="">Load preset…</option>
            {TOU_PRESET_REGIONS.map((region) => {
              const inRegion = TOU_PRESETS.filter((p) => p.region === region);
              if (inRegion.length === 0) return null;
              return (
                <optgroup key={region} label={region}>
                  {inRegion.map((p) => (
                    <option key={p.id} value={p.id}>{p.display_name}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>

          {/* Replace vs Append — wipe current state or merge into it.
              Tight segmented toggle next to the dropdown so the
              operator's choice is visible before they pick a preset. */}
          <div className={styles.clockToggle} role="group" aria-label="Preset apply mode">
            <button
              type="button"
              className={`${styles.clockToggleBtn} ${presetMode === 'replace' ? styles.clockToggleBtnActive : ''}`}
              onClick={() => setPresetMode('replace')}
              aria-pressed={presetMode === 'replace'}
              title="Wipe current windows and load preset"
            >
              Replace
            </button>
            <button
              type="button"
              className={`${styles.clockToggleBtn} ${presetMode === 'append' ? styles.clockToggleBtnActive : ''}`}
              onClick={() => setPresetMode('append')}
              aria-pressed={presetMode === 'append'}
              title="Add preset windows on top of current ones"
            >
              Append
            </button>
          </div>

          <CopyDayMenu
            value={value}
            onChange={onChange}
            open={copyDayOpen}
            setOpen={setCopyDayOpen}
            disabled={disabled}
          />
        </div>

        <div className={styles.headerRight}>
          <div className={styles.clockToggle} role="group" aria-label="Clock format">
            <button
              type="button"
              className={`${styles.clockToggleBtn} ${clockMode === '12h' ? styles.clockToggleBtnActive : ''}`}
              onClick={() => setClockMode('12h')}
              aria-pressed={clockMode === '12h'}
            >
              12h
            </button>
            <button
              type="button"
              className={`${styles.clockToggleBtn} ${clockMode === '24h' ? styles.clockToggleBtnActive : ''}`}
              onClick={() => setClockMode('24h')}
              aria-pressed={clockMode === '24h'}
            >
              24h
            </button>
          </div>
          <button
            type="button"
            className={`${styles.headerBtn} ${styles.headerBtnPrimary}`}
            onClick={add}
            disabled={disabled}
          >
            <Plus size={14} /> Add window
          </button>
        </div>
      </div>

      {appliedPreset && (
        <p className={styles.presetNote}>
          <strong>{appliedPreset.display_name}</strong> — {appliedPreset.utility}.
          {' '}{appliedPreset.notes}
          <br />
          Verify rates with the utility before saving. Last researched{' '}
          <strong>{appliedPreset.last_verified}</strong>.
        </p>
      )}

      {/* Validation banner — only shown when issues are present. */}
      {issues.length > 0 && (
        <div className={styles.validationBanner}>
          <strong>{issues.length} validation issue{issues.length === 1 ? '' : 's'}.</strong>{' '}
          Saves with these will be rejected by the server.
        </div>
      )}

      {/* Row list */}
      <div className={styles.rowList}>
        {value.length === 0 && (
          <div className={styles.emptyMsg}>
            No windows yet — add one or load a preset.
          </div>
        )}
        {value.map((row, idx) => {
          const rowIssues = issuesByIndex.get(idx) ?? [];
          const endHourIssue = rowIssues.find((it) => it.field === 'end_hour');
          const rateIssue = rowIssues.find((it) => it.field === 'rate_cents');
          const rateErrorId = `${idPrefix}-rate-error-${idx}`;
          const overlapIssue = rowIssues.find((it) => it.field === 'days_overlap');
          const overlapDays = overlapIssue?.days ?? [];
          return (
            <div
              key={idx}
              className={`${styles.row} ${highlightIdx === idx ? styles.rowHighlighted : ''}`}
            >
              {/* Day pills */}
              <div className={styles.dayPills} role="group" aria-label="Days of week">
                {DAY_LABELS.map((label, dow) => {
                  const active = row.day_of_week.includes(dow);
                  const errored = active && overlapDays.includes(dow);
                  return (
                    <button
                      key={label}
                      type="button"
                      className={[
                        styles.dayPill,
                        active ? styles.dayPillActive : '',
                        errored ? styles.dayPillError : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => toggleDay(idx, dow)}
                      disabled={disabled}
                      title={errored ? overlapIssue?.message : undefined}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Start hour */}
              <div className={styles.hourCell}>
                <span className={styles.hourCellLabel}>Start</span>
                <HourInput
                  value={row.start_hour}
                  onCommit={(h) => update(idx, { start_hour: Math.min(23, Math.max(0, h)) })}
                  clockMode={clockMode}
                  disabled={disabled}
                />
              </div>

              {/* End hour */}
              <div className={styles.hourCell}>
                <span className={styles.hourCellLabel}>End</span>
                <HourInput
                  value={row.end_hour}
                  onCommit={(h) => update(idx, { end_hour: Math.min(24, Math.max(1, h)) })}
                  clockMode={clockMode}
                  disabled={disabled}
                  error={!!endHourIssue}
                />
                {endHourIssue && (
                  <span className={styles.fieldError}>{endHourIssue.message}</span>
                )}
              </div>

              {/* Rate + unit */}
              <div className={styles.rateCell}>
                <span className={styles.hourCellLabel}>Rate / unit</span>
                <div className={styles.rateInputRow}>
                  <RateInput
                    value={row.rate_cents}
                    unit={row.rate_type}
                    onCommit={(cents) => update(idx, { rate_cents: cents })}
                    disabled={disabled}
                    error={!!rateIssue}
                    describedBy={rateIssue ? rateErrorId : undefined}
                  />
                  <select
                    className={styles.rateUnit}
                    value={row.rate_type}
                    onChange={(e) => update(idx, {
                      rate_type: e.target.value === 'per_hour' ? 'per_hour' : 'per_kwh',
                    })}
                    disabled={disabled}
                    aria-label="Rate unit"
                  >
                    <option value="per_kwh">¢/kWh</option>
                    <option value="per_hour">¢/hour</option>
                  </select>
                </div>
                {rateIssue && (
                  <span id={rateErrorId} className={styles.fieldError}>{rateIssue.message}</span>
                )}
              </div>

              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => remove(idx)}
                disabled={disabled}
                aria-label="Remove window"
                title="Remove window"
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Schedule preview heatmap */}
      {value.length > 0 && (
        <SchedulePreview
          windows={value}
          onCellClick={(idx) => {
            setHighlightIdx(idx);
            // Auto-clear highlight after a short timeout so it doesn't
            // stick visually forever; long enough to see the row it
            // points to.
            window.setTimeout(() => setHighlightIdx(null), 2000);
          }}
        />
      )}
    </div>
  );
}

// ── HourInput (12/24 aware) ────────────────────────────────────────

function HourInput({
  value, onCommit, clockMode, disabled = false, error = false,
}: {
  value: number;
  onCommit: (h: number) => void;
  clockMode: '12h' | '24h';
  disabled?: boolean;
  error?: boolean;
}) {
  // Two states: the canonical numeric (passed in from parent) and a
  // local draft string while the user is typing. We commit on blur or
  // Enter — typing is decoupled from the parent's onChange so the
  // input doesn't fight the formatter mid-keystroke.
  const [draft, setDraft] = useState<string>(() => formatHour(value, clockMode));
  // Re-seed draft when external value changes (e.g. preset load).
  useEffect(() => {
    setDraft(formatHour(value, clockMode));
  }, [value, clockMode]);

  const commit = (): void => {
    const parsed = parseHour(draft);
    if (parsed === null) {
      // Restore previous canonical value on invalid input.
      setDraft(formatHour(value, clockMode));
      return;
    }
    if (parsed !== value) onCommit(parsed);
    setDraft(formatHour(parsed, clockMode));
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      className={`${styles.hourInput} ${error ? styles.hourInputError : ''}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      disabled={disabled}
    />
  );
}

// ── RateInput (smart parser + unit-aware formatter) ─────────────────

function RateInput({
  value, unit, onCommit, disabled = false, error = false, describedBy,
}: {
  value: number;
  unit: 'per_kwh' | 'per_hour';
  onCommit: (cents: number) => void;
  disabled?: boolean;
  error?: boolean;
  /** id of the row's rate error, when there is one. */
  describedBy?: string;
}) {
  // Default draft is the raw cents number (no suffix) so the input is
  // immediately editable. On blur we normalize via formatRate.
  const [draft, setDraft] = useState<string>(() => String(value));
  useEffect(() => {
    setDraft(String(value));
  }, [value, unit]);

  const commit = (): void => {
    const parsed = parseRate(draft);
    if (!parsed.valid) {
      // Restore on invalid input.
      setDraft(String(value));
      return;
    }
    if (parsed.cents !== value) onCommit(parsed.cents);
    setDraft(formatRate(parsed.cents, unit));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`${styles.rateInput} ${error ? styles.rateInputError : ''}`}
      aria-invalid={error || undefined}
      aria-describedby={describedBy}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onFocus={(e) => {
        // On focus, strip the formatter suffix so the user can edit
        // the raw number without selecting around "/kWh" cruft.
        const parsed = parseRate(draft);
        if (parsed.valid) {
          setDraft(parsed.cents % 1 === 0 ? String(parsed.cents) : parsed.cents.toFixed(2));
        }
        e.target.select();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      disabled={disabled}
      aria-label="Rate"
    />
  );
}

// ── Copy-day popover ───────────────────────────────────────────────

function CopyDayMenu({
  value, onChange, open, setOpen, disabled,
}: {
  value: ElectricityTouWindowAdmin[];
  onChange: (next: ElectricityTouWindowAdmin[]) => void;
  open: boolean;
  setOpen: (next: boolean) => void;
  disabled: boolean;
}) {
  const [sourceDay, setSourceDay] = useState<number>(1); // Mon by default
  const [targetDays, setTargetDays] = useState<number[]>([]);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: PointerEvent): void {
      if (!popoverRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, [open, setOpen]);

  const toggleTarget = (d: number): void => {
    setTargetDays((prev) => (prev.includes(d)
      ? prev.filter((x) => x !== d)
      : [...prev, d].sort((a, b) => a - b)));
  };

  const sourceWindows = value.filter((w) => w.day_of_week.includes(sourceDay));
  // Targets that already have ANY coverage — used to warn about
  // potential conflicts on apply. Doesn't block the operation; the
  // overlap validator will surface specifics post-apply.
  const targetsWithExistingCoverage = targetDays.filter((d) => value.some(
    (w) => w.day_of_week.includes(d) && !w.day_of_week.includes(sourceDay),
  ));

  const apply = (): void => {
    if (sourceWindows.length === 0 || targetDays.length === 0) {
      setOpen(false);
      return;
    }
    // For each source window, add the target days to its day_of_week
    // (sorted, deduped). Skip target days already present on that
    // window. This is an additive operation — never removes coverage.
    const next = value.map((w) => {
      if (!w.day_of_week.includes(sourceDay)) return w;
      const merged = Array.from(new Set([...w.day_of_week, ...targetDays]))
        .sort((a, b) => a - b);
      return { ...w, day_of_week: merged };
    });
    onChange(next);
    setOpen(false);
    setTargetDays([]);
  };

  return (
    <div className={styles.popoverHost}>
      <button
        type="button"
        className={styles.headerBtn}
        onClick={() => setOpen(!open)}
        disabled={disabled || value.length === 0}
      >
        <Copy size={14} /> Copy day
      </button>
      {open && (
        <div className={styles.popover} ref={popoverRef}>
          <div className={styles.popoverRow}>
            <span className={styles.popoverLabel}>Copy from</span>
            <select
              className={styles.headerSelect}
              value={sourceDay}
              onChange={(e) => setSourceDay(parseInt(e.target.value, 10))}
            >
              {DAY_LABELS.map((label, dow) => (
                <option key={label} value={dow}>{label}</option>
              ))}
            </select>
            <span className={styles.popoverNote}>
              {sourceWindows.length} window{sourceWindows.length === 1 ? '' : 's'} on {DAY_LABELS[sourceDay]}.
            </span>
          </div>
          <div className={styles.popoverRow}>
            <span className={styles.popoverLabel}>To</span>
            <div className={styles.dayPills}>
              {DAY_LABELS.map((label, dow) => {
                if (dow === sourceDay) return null;
                const active = targetDays.includes(dow);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`${styles.dayPill} ${active ? styles.dayPillActive : ''}`}
                    onClick={() => toggleTarget(dow)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {targetsWithExistingCoverage.length > 0 && (
            <p className={styles.popoverNote}>
              Note: {targetsWithExistingCoverage.map((d) => DAY_LABELS[d]).join(', ')}{' '}
              already have other coverage. Copying may produce overlap — the
              validator will surface conflicts.
            </p>
          )}
          <div className={styles.popoverActions}>
            <button
              type="button"
              className={styles.headerBtn}
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.headerBtn} ${styles.headerBtnPrimary}`}
              onClick={apply}
              disabled={sourceWindows.length === 0 || targetDays.length === 0}
            >
              Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Schedule preview heatmap ────────────────────────────────────────

function SchedulePreview({
  windows, onCellClick,
}: {
  windows: ElectricityTouWindowAdmin[];
  onCellClick: (windowIdx: number) => void;
}) {
  // Two-scale color: blue for per_kwh, orange for per_hour. Intensity
  // scaled within each type's min/max so a mixed schedule reads cleanly.
  const { kwhMin, kwhMax, hourMin, hourMax } = useMemo(() => {
    let kLo = Infinity, kHi = -Infinity, hLo = Infinity, hHi = -Infinity;
    for (const w of windows) {
      if (w.rate_type === 'per_kwh') {
        if (w.rate_cents < kLo) kLo = w.rate_cents;
        if (w.rate_cents > kHi) kHi = w.rate_cents;
      } else {
        if (w.rate_cents < hLo) hLo = w.rate_cents;
        if (w.rate_cents > hHi) hHi = w.rate_cents;
      }
    }
    return {
      kwhMin: kLo === Infinity ? 0 : kLo,
      kwhMax: kHi === -Infinity ? 0 : kHi,
      hourMin: hLo === Infinity ? 0 : hLo,
      hourMax: hHi === -Infinity ? 0 : hHi,
    };
  }, [windows]);

  function cellColor(w: ElectricityTouWindowAdmin): string {
    if (w.rate_type === 'per_hour') {
      const t = hourMax > hourMin
        ? (w.rate_cents - hourMin) / (hourMax - hourMin)
        : 0.5;
      // Orange family: light (30 hue, low sat) → saturated (20, high sat)
      return `hsl(28 ${(40 + t * 50).toFixed(0)}% ${(70 - t * 20).toFixed(0)}%)`;
    }
    const t = kwhMax > kwhMin
      ? (w.rate_cents - kwhMin) / (kwhMax - kwhMin)
      : 0.5;
    // Blue family
    return `hsl(210 ${(40 + t * 50).toFixed(0)}% ${(70 - t * 20).toFixed(0)}%)`;
  }

  // Map (day, hour) → window index. For overlapping windows the LAST
  // one wins visually; the validator surfaces the overlap separately.
  const coverage = useMemo(() => {
    const m = new Map<string, number>(); // "d-h" → window index
    windows.forEach((w, idx) => {
      for (const d of w.day_of_week) {
        for (let h = w.start_hour; h < w.end_hour; h += 1) {
          m.set(`${d}-${h}`, idx);
        }
      }
    });
    return m;
  }, [windows]);

  // Cell label — abbreviated rate, "20¢" or "$1.50/h".
  function cellLabel(w: ElectricityTouWindowAdmin): string {
    if (w.rate_type === 'per_hour') {
      return w.rate_cents >= 100
        ? `$${(w.rate_cents / 100).toFixed(0)}/h`
        : `${Math.round(w.rate_cents)}¢/h`;
    }
    return `${Math.round(w.rate_cents)}¢`;
  }

  return (
    <div className={styles.preview}>
      <span className={styles.previewTitle}>Schedule preview</span>
      <div className={styles.previewGrid}>
        {/* Header row: corner + 7 day labels */}
        <div className={styles.previewCornerCell} aria-hidden />
        {DAY_LABELS.map((label) => (
          <div key={label} className={styles.previewDayHeader}>{label}</div>
        ))}

        {/* 24 hour rows, each with a label cell + 7 day cells */}
        {Array.from({ length: 24 }).map((_, h) => {
          const cells: JSX.Element[] = [
            <div key={`label-${h}`} className={styles.previewHourLabel}>
              {h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`}
            </div>,
          ];
          for (let d = 0; d < 7; d += 1) {
            const idx = coverage.get(`${d}-${h}`);
            if (idx === undefined) {
              cells.push(<div key={`${d}-${h}`} className={styles.previewCell} />);
              continue;
            }
            const w = windows[idx]!;
            cells.push(
              <div
                key={`${d}-${h}`}
                className={`${styles.previewCell} ${styles.previewCellFilled}`}
                style={{ background: cellColor(w) }}
                onClick={() => onCellClick(idx)}
                title={`${DAY_LABELS[d]} ${h}-${h + 1}h: ${cellLabel(w)}`}
              >
                {cellLabel(w)}
              </div>,
            );
          }
          return cells;
        })}
      </div>
    </div>
  );
}

export default LocationTouPicker;
